/**
 * User API service
 */

import { apiClient } from './api-client';
import type { User, UserCreateRequest, UserUpdateRequest, UserStatistics, UserFilterOptions  } from '../types/user';
import type { PaginationOptions, PaginatedResponse } from '../types/pagination';
import type { ApiResponse } from '../types/api';

interface UserListResponse {
  items: any[];
  nextToken?: string;
  hasMore: boolean;
}

function mapUserFromApi(apiUser: any): User {
  return {
    id: apiUser.userId,
    name: apiUser.name,
    email: apiUser.email,
    status: apiUser.status,
    roles: apiUser.roles,
    insertTimestamp: apiUser.insertTimestamp,
    lastModifiedTimestamp: apiUser.lastModifiedTimestamp,
  };
}

export class UserService {
  private readonly basePath = '/api/users';

  async getUsers(options?: PaginationOptions, filters?: UserFilterOptions): Promise<PaginatedResponse<User>> {
    const params = new URLSearchParams();
    
    if (options?.limit) {
      params.append('limit', options.limit.toString());
    }
    if (options?.nextToken) {
      params.append('nextToken', options.nextToken);
    }
    
    if (filters?.name) {
      params.append('name', filters.name);
    }
    if (filters?.email) {
      params.append('email', filters.email);
    }
    if (filters?.status) {
      params.append('status', filters.status);
    }
    if (filters?.role) {
      params.append('role', filters.role);
    }
    
    const url = params.toString() ? `${this.basePath}?${params}` : this.basePath;
    const response = await apiClient.get<ApiResponse<UserListResponse>>(url);
    
    return {
      items: response.data?.items ? response.data.items.map(mapUserFromApi) : [],
      nextToken: response.data?.nextToken,
      hasMore: response.data?.hasMore ?? false
    };
  }

  async getUser(id: string): Promise<User> {
    const response = await apiClient.get<ApiResponse<any>>(`${this.basePath}/${id}`);
    return mapUserFromApi(response.data);
  }

  async createUser(data: UserCreateRequest): Promise<User> {
    const response = await apiClient.post<ApiResponse<any>>(this.basePath, data);
    return mapUserFromApi(response.data);
  }

  async updateUser(id: string, data: UserUpdateRequest): Promise<User> {
    const response = await apiClient.put<ApiResponse<any>>(`${this.basePath}/${id}`, data);
    return mapUserFromApi(response.data);
  }

  async deleteUser(id: string): Promise<void> {
    await apiClient.delete<ApiResponse<void>>(`${this.basePath}/${id}`);
  }

  async getUserStatistics(filters?: UserFilterOptions): Promise<UserStatistics> {
    const params = new URLSearchParams();
    
    if (filters?.name) {
      params.append('name', filters.name);
    }
    if (filters?.email) {
      params.append('email', filters.email);
    }
    if (filters?.status) {
      params.append('status', filters.status);
    }
    if (filters?.role) {
      params.append('role', filters.role);
    }
    
    const url = params.toString() ? `${this.basePath}/statistics?${params}` : `${this.basePath}/statistics`;
    const response = await apiClient.get<ApiResponse<UserStatistics>>(url);
    if (!response.data) {
      throw new Error('User statistics data is undefined');
    }
    return response.data;
  }
}

export const userService = new UserService();
